// Floating alignment toolbar. Shows whenever ANY object is selected on the
// canvas (single or multiple). Alignment is RELATIVE TO THE PRINT AREA
// (passed in via the `bounds` prop) — that's what matters in print design,
// not selection bounds. Distribute is only shown for 3+ objects.
//
// Mirrors the TextToolbar positioning model so both can co-exist (alignment
// stacks above text if both are visible).
//
// Layout: TWO rows.
//   Row 1 — live size + DPI readout (mm × mm and effective DPI).
//   Row 2 — alignment / distribute / fit-width / delete buttons.
// When the effective DPI is below the "poor" threshold the whole toolbar
// turns red and shows a "Low DPI — print may look blurry" notice, so the
// customer sees the warning immediately instead of having to read tiny
// orange/red numbers.
import { useEffect, useState } from 'react'
import { fabric } from 'fabric'

interface Bounds { left: number; top: number; width: number; height: number }

interface Props {
  canvas: fabric.Canvas | null
  tick: number
  /** The print area / sheet bounds in canvas pixels. Alignment math uses this. */
  bounds: Bounds | null
  /** When true, shifts the toolbar up by TEXT_TOOLBAR_H px so it clears the TextToolbar. */
  stackAboveTextToolbar?: boolean
  /** Live width / height (mm) + effective DPI for the active image. Shown
   *  on its own row at the top of the toolbar so the customer doesn't need
   *  to hunt for the readout. Pass null to hide the readout row. */
  sizeInfo?: { widthMm: number; heightMm: number; ppi: number } | null
}

// DPI tiers — single source of truth, used by both the readout chip and
// the bar's "is this poor?" state.
//   ≥ 250  Excellent (emerald)
//   ≥ 200  Good      (amber)
//   ≥ 150  OK-ish    (orange)
//   < 150  Poor      (red) → turns the whole toolbar red
const DPI_POOR_THRESHOLD = 150

const TEXT_TOOLBAR_H = 108

export default function AlignmentToolbar({ canvas, tick, bounds, stackAboveTextToolbar = false, sizeInfo = null }: Props) {
  const yOffset = stackAboveTextToolbar ? TEXT_TOOLBAR_H : 0
  const [active, setActive] = useState<fabric.Object | null>(null)

  useEffect(() => {
    if (!canvas) { setActive(null); return }
    const a = canvas.getActiveObject()
    setActive(a ?? null)
  }, [canvas, tick])

  if (!active || !bounds) return null

  const isSelection = active.type === 'activeSelection'
  const objs: fabric.Object[] = isSelection ? [...(active as fabric.ActiveSelection).getObjects()] : [active]
  const right  = bounds.left + bounds.width
  const bottom = bounds.top + bounds.height
  const centerX = bounds.left + bounds.width / 2
  const centerY = bounds.top  + bounds.height / 2

  // Move each object's left/top so its bounding-rect edge lands at `target`.
  function delta(obj: fabric.Object, axis: 'x' | 'y', target: number, edge: 'min' | 'max' | 'centre'): number {
    const r = obj.getBoundingRect(true, true)
    const current = edge === 'min'    ? (axis === 'x' ? r.left : r.top)
                  : edge === 'max'    ? (axis === 'x' ? r.left + r.width : r.top + r.height)
                                      : (axis === 'x' ? r.left + r.width / 2 : r.top + r.height / 2)
    return target - current
  }

  function apply(transform: () => void) {
    if (!canvas) return
    // Discard the active selection so individual positions go back to
    // world-absolute before we modify them; restore at the end.
    if (isSelection) canvas.discardActiveObject()
    transform()
    objs.forEach((o) => o.setCoords())
    if (isSelection) {
      const newSel = new fabric.ActiveSelection(objs, { canvas })
      canvas.setActiveObject(newSel)
    }
    canvas.fire('object:modified', { target: isSelection ? canvas.getActiveObject() : active })
    canvas.requestRenderAll()
  }

  const ops = {
    alignLeft:    () => apply(() => objs.forEach((o) => o.set({ left: (o.left ?? 0) + delta(o, 'x', bounds.left, 'min') }))),
    alignCenterH: () => apply(() => objs.forEach((o) => o.set({ left: (o.left ?? 0) + delta(o, 'x', centerX, 'centre') }))),
    alignRight:   () => apply(() => objs.forEach((o) => o.set({ left: (o.left ?? 0) + delta(o, 'x', right, 'max') }))),
    alignTop:     () => apply(() => objs.forEach((o) => o.set({ top: (o.top ?? 0) + delta(o, 'y', bounds.top, 'min') }))),
    alignCenterV: () => apply(() => objs.forEach((o) => o.set({ top: (o.top ?? 0) + delta(o, 'y', centerY, 'centre') }))),
    alignBottom:  () => apply(() => objs.forEach((o) => o.set({ top: (o.top ?? 0) + delta(o, 'y', bottom, 'max') }))),
    distributeH:  () => {
      if (objs.length < 3) return
      apply(() => {
        const sorted = [...objs].sort((a, c) => a.getBoundingRect(true, true).left - c.getBoundingRect(true, true).left)
        const totalWidth = sorted.reduce((acc, o) => acc + o.getBoundingRect(true, true).width, 0)
        const gap = Math.max(0, (bounds.width - totalWidth) / (sorted.length - 1))
        let cursor = bounds.left
        sorted.forEach((o) => {
          o.set({ left: (o.left ?? 0) + delta(o, 'x', cursor, 'min') })
          cursor += o.getBoundingRect(true, true).width + gap
        })
      })
    },
    distributeV: () => {
      if (objs.length < 3) return
      apply(() => {
        const sorted = [...objs].sort((a, c) => a.getBoundingRect(true, true).top - c.getBoundingRect(true, true).top)
        const totalHeight = sorted.reduce((acc, o) => acc + o.getBoundingRect(true, true).height, 0)
        const gap = Math.max(0, (bounds.height - totalHeight) / (sorted.length - 1))
        let cursor = bounds.top
        sorted.forEach((o) => {
          o.set({ top: (o.top ?? 0) + delta(o, 'y', cursor, 'min') })
          cursor += o.getBoundingRect(true, true).height + gap
        })
      })
    },
    fitWidth: () => apply(() => objs.forEach((o) => {
      const r = o.getBoundingRect(true, true)
      const factor = bounds.width / r.width
      o.scaleX = (o.scaleX ?? 1) * factor
      o.scaleY = (o.scaleY ?? 1) * factor
      o.setCoords()
      const r2 = o.getBoundingRect(true, true)
      o.set({ left: (o.left ?? 0) + (bounds.left - r2.left) })
    })),
  }

  // Larger buttons + larger icons. When the bar is in "poor DPI" mode the
  // button surfaces stay white so the icons remain legible against the
  // red chrome.
  const isPoorDpi = !!sizeInfo && sizeInfo.ppi > 0 && sizeInfo.ppi < DPI_POOR_THRESHOLD

  const Btn = ({ on, title, children, disabled = false }: { on: () => void; title: string; children: React.ReactNode; disabled?: boolean }) => (
    <button onClick={on} title={title} disabled={disabled}
      className={'w-10 h-10 rounded flex items-center justify-center transition-colors ' + (
        disabled ? 'text-ink-4 cursor-not-allowed' :
        isPoorDpi ? 'text-ink-2 bg-white hover:bg-red-50 hover:text-red-700 border border-red-200' :
                    'text-ink-2 hover:bg-surface hover:text-ink')}>
      {children}
    </button>
  )

  const I = (paths: React.ReactNode) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">{paths}</svg>
  )

  // DPI badge — chip-style so the colour swatch reads at a glance.
  const dpiTier =
    !sizeInfo ? null :
    sizeInfo.ppi >= 250 ? { label: 'Excellent', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' } :
    sizeInfo.ppi >= 200 ? { label: 'Good',      cls: 'bg-amber-100   text-amber-900   border-amber-300'   } :
    sizeInfo.ppi >= 150 ? { label: 'OK',        cls: 'bg-orange-100  text-orange-900  border-orange-300'  } :
                          { label: 'Poor',      cls: 'bg-red-200     text-red-900     border-red-400'     }

  return (
    <div
      className={[
        'fixed z-40 rounded-lg shadow-xl px-3 py-2.5 flex flex-col gap-1.5 border-2',
        isPoorDpi ? 'bg-red-50 border-red-500' : 'bg-white border-border',
      ].join(' ')}
      style={{ bottom: 80 + yOffset, left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}>

      {/* ── Row 1: size + DPI readout (own line, big & legible) ───── */}
      {sizeInfo && (
        <div className="flex items-center gap-3 px-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] uppercase tracking-[0.16em] font-bold text-ink-3">Size</span>
            <span className="font-mono text-sm text-ink font-semibold">
              {sizeInfo.widthMm} × {sizeInfo.heightMm} <span className="text-ink-3 font-normal text-xs">mm</span>
            </span>
          </div>
          <span className="w-px h-6 bg-border" />
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] uppercase tracking-[0.16em] font-bold text-ink-3">DPI</span>
            <span className="font-mono text-sm text-ink font-semibold">{sizeInfo.ppi}</span>
            {dpiTier && (
              <span className={'text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ' + dpiTier.cls}
                title="300+ DPI is preferred for sharp prints">
                {dpiTier.label}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Low-DPI warning banner — only shown when ppi < 150 ──── */}
      {isPoorDpi && (
        <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-red-100 text-red-900 text-[11px] font-semibold border border-red-300">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9"  x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          Low DPI — print may look blurry. Make the design smaller or upload a higher-resolution file.
        </div>
      )}

      {/* ── Row 2: alignment / distribute / fit / delete ──────────── */}
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase tracking-wider text-ink-3 mr-1 px-1" title="Align relative to print area">Align ⇆ area</span>
        <Btn on={ops.alignLeft}    title="Align left (to print area)">       {I(<><line x1="4" y1="4" x2="4" y2="20"/><rect x="6" y="6" width="12" height="4"/><rect x="6" y="14" width="8" height="4"/></>)}</Btn>
        <Btn on={ops.alignCenterH} title="Align centre H (to print area)">   {I(<><line x1="12" y1="4" x2="12" y2="20"/><rect x="6" y="6" width="12" height="4"/><rect x="8" y="14" width="8" height="4"/></>)}</Btn>
        <Btn on={ops.alignRight}   title="Align right (to print area)">      {I(<><line x1="20" y1="4" x2="20" y2="20"/><rect x="6" y="6" width="12" height="4"/><rect x="10" y="14" width="8" height="4"/></>)}</Btn>
        <span className="w-px h-7 bg-border mx-1" />
        <Btn on={ops.alignTop}     title="Align top (to print area)">        {I(<><line x1="4" y1="4" x2="20" y2="4"/><rect x="6" y="6" width="4" height="12"/><rect x="14" y="6" width="4" height="8"/></>)}</Btn>
        <Btn on={ops.alignCenterV} title="Align centre V (to print area)">   {I(<><line x1="4" y1="12" x2="20" y2="12"/><rect x="6" y="6" width="4" height="12"/><rect x="14" y="8" width="4" height="8"/></>)}</Btn>
        <Btn on={ops.alignBottom}  title="Align bottom (to print area)">     {I(<><line x1="4" y1="20" x2="20" y2="20"/><rect x="6" y="6" width="4" height="12"/><rect x="14" y="10" width="4" height="8"/></>)}</Btn>
        <span className="w-px h-7 bg-border mx-1" />
        <Btn on={ops.fitWidth}    title="Fit width of print area">           {I(<><line x1="4" y1="4" x2="4" y2="20"/><line x1="20" y1="4" x2="20" y2="20"/><line x1="4" y1="12" x2="20" y2="12"/></>)}</Btn>
        <Btn on={ops.distributeH} title="Distribute horizontally in print area" disabled={objs.length < 3}>{I(<><rect x="3"  y="8" width="4" height="8"/><rect x="10" y="8" width="4" height="8"/><rect x="17" y="8" width="4" height="8"/></>)}</Btn>
        <Btn on={ops.distributeV} title="Distribute vertically in print area"  disabled={objs.length < 3}>{I(<><rect x="8" y="3"  width="8" height="4"/><rect x="8" y="10" width="8" height="4"/><rect x="8" y="17" width="8" height="4"/></>)}</Btn>
        <span className="w-px h-7 bg-border mx-1" />
        {/* Multi-aware delete — pulls every contained object out of an
            ActiveSelection before removing so it actually clears the canvas. */}
        <button
          onClick={() => {
            if (!canvas) return
            const a = canvas.getActiveObject(); if (!a) return
            if (a.type === 'activeSelection') {
              const grp = a as fabric.ActiveSelection
              const items = grp.getObjects().slice()
              grp.remove(...items)
              canvas.discardActiveObject()
              items.forEach((o) => canvas.remove(o))
            } else {
              canvas.remove(a)
            }
            canvas.requestRenderAll()
          }}
          title={isSelection ? `Delete ${objs.length} selected items` : 'Delete selected'}
          className="w-10 h-10 rounded flex items-center justify-center text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors">
          {I(<><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></>)}
        </button>
      </div>
    </div>
  )
}
